import { cn } from "@/utils/cn";
import { InfoTooltip } from "./InfoTooltip";
import { SvgPieChart } from "./SvgPieChart";
import type { PieChartModel } from "@/types/dashboard";

interface PieChartSectionProps {
  title: string;
  tooltipText: string;
  /** Base data (business level) */
  data: PieChartModel[];
  /** Drilldown data (business-line level) — shown when a business is selected */
  businessLineData: PieChartModel[];
  allBusinesses: string[];
  otherHoverItems?: string[];
  /** When true, the per-chart dropdown is disabled (top filter is active) */
  disabled: boolean;
  selectedBusiness: string | null;
  onBusinessChange: (business: string | null) => void;
}

export function PieChartSection({
  title,
  tooltipText,
  data,
  businessLineData,
  allBusinesses,
  otherHoverItems = [],
  disabled,
  selectedBusiness,
  onBusinessChange
}: PieChartSectionProps) {
  const displayData =
    selectedBusiness && businessLineData.length > 0 ? businessLineData : data;

  const id = title.replace(/\s+/g, "-").toLowerCase();

  return (
    <div className="chart-box pie-chart-box">
      <header className="box-header">
        <div className="box-title">
          <span>{title}</span>
          <InfoTooltip text={tooltipText} />
        </div>

        <div className="chart-controls">
          <label htmlFor={id} className="sr-only">
            Filter {title} by business
          </label>
          <select
            id={id}
            value={selectedBusiness ?? ""}
            onChange={(e) => onBusinessChange(e.target.value || null)}
            disabled={disabled}
            className={cn(
              "dropdown",
              disabled && "cursor-not-allowed opacity-50"
            )}
          >
            <option value="">All Businesses</option>
            {allBusinesses.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>
      </header>

      <div className="pie-chart-content">
        <ul className="pie-legend">
          {displayData.map((seg) => (
            <li key={seg.segmentName} className="legend-item">
              <span
                className="legend-color"
                style={{ backgroundColor: seg.segmentColor }}
                aria-hidden="true"
              />
              <span>{seg.segmentName}</span>
            </li>
          ))}
        </ul>

        <div className="pie-chart-container">
          <SvgPieChart
            data={displayData}
            otherHoverItems={otherHoverItems}
            selectedBusiness={selectedBusiness}
          />
        </div>
      </div>
    </div>
  );
}
